import { z } from 'zod';
import { identifierReferenceSchema, locationSnapshotSchema } from './location.js';
import { canonicalJsonStringify, recordedMetresSchema, revisionSchema, sha256Schema, uuidSchema, } from './primitives.js';
import { MAX_ROUTE_VIA_POINTS, routeSeedSchema } from './route.js';
import { artifactVersionSchema } from './versioning.js';
export const routeDraftCountryCodeSchema = z.enum(['CZ', 'DE']);
export const routeDraftDistanceInputModeSchema = z.enum(['none', 'inherited', 'manual_override']);
export const routeDraftCatalogDocumentIdSchema = z
    .string()
    .min(1)
    .max(256)
    .refine((value) => value.trim().length > 0, 'catalog document ID must not be blank');
export const routeDraftOfferSourceSchema = z.enum(['history', 'template', 'network']);
/**
 * Expand/contract release switch. The compatibility reader understands the
 * expanded wire but does not introduce it into a database that is still
 * expected to roll back to the pre-expansion production reader.
 */
export const ROUTE_DRAFT_WIRE_WRITE_MODE = 'compatibility_reader';
/**
 * Durable, non-authoritative identity of the exact live segment snapshot used
 * by the copy shortcut. Consumers must revalidate every field against the
 * current source segment before projecting or committing copied route data.
 */
export const routeCopySourceProofSchema = z.strictObject({
    proof_contract: z.literal('dmf.route-copy-source-proof.v1'),
    source_shift_id: uuidSchema,
    source_trip_segment_id: uuidSchema,
    source_trip_segment_revision: revisionSchema,
    source_route_variant_id: uuidSchema,
    source_route_snapshot_sha256: sha256Schema,
});
export const routeDraftSelectionSchema = z
    .discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('manual') }),
    z.strictObject({
        kind: z.literal('offer'),
        offer_source: routeDraftOfferSourceSchema,
        offer_id: z.string().min(1).max(256),
        selection_method: z.enum(['automatic', 'explicit']),
    }),
])
    .superRefine((value, context) => {
    if (value.kind === 'offer' &&
        value.selection_method === 'automatic' &&
        value.offer_source !== 'history') {
        context.addIssue({
            code: 'custom',
            path: ['selection_method'],
            message: 'only an exact previously confirmed history offer can be automatically selected',
        });
    }
});
/**
 * Raw endpoint state that remains truthful while a draft or cancelled segment
 * is incomplete. A carried immutable snapshot is retained verbatim; manual
 * text is deliberately not trimmed or projected into a Location until valid.
 */
export const routeDraftEndpointSchema = z
    .strictObject({
    location_id: uuidSchema,
    name: z.string().max(512),
    country_code: routeDraftCountryCodeSchema,
    key_identifier: identifierReferenceSchema.nullable(),
    carried_location_snapshot: locationSnapshotSchema.nullable(),
    catalog_document_id: routeDraftCatalogDocumentIdSchema.nullable(),
    catalog_version: artifactVersionSchema.nullable(),
})
    .superRefine((value, context) => {
    validateCarriedLocationSnapshot(value, context);
    validateRouteDraftCatalogProvenancePair(value, context);
    if (value.catalog_document_id !== null && value.carried_location_snapshot === null) {
        context.addIssue({
            code: 'custom',
            path: ['carried_location_snapshot'],
            message: 'a selected catalog document requires its immutable location snapshot',
        });
    }
});
/**
 * Status-specific route state for draft/cancelled TripSegments. It never
 * contains confirmation evidence and therefore can never be consumed as route
 * learning. Complete/partial records continue to use route_snapshot.v1.
 */
const routeDraftSnapshotV1Schema = z
    .strictObject({
    snapshot_version: z.literal('route_draft_snapshot.v1'),
    route_variant_id: uuidSchema,
    origin: routeDraftEndpointSchema,
    via: z.array(routeDraftEndpointSchema).max(MAX_ROUTE_VIA_POINTS),
    destination: routeDraftEndpointSchema.nullable(),
    route_selection: routeDraftSelectionSchema.nullable(),
    route_seed: routeSeedSchema.nullable(),
    distance_input_mode: routeDraftDistanceInputModeSchema,
    distance_metres: recordedMetresSchema.nullable(),
})
    .superRefine((value, context) => {
    validateRouteSeedBinding(value, context);
    validateDistanceBinding(value, context);
});
const routeDraftSnapshotV2Schema = z
    .strictObject({
    snapshot_version: z.literal('route_draft_snapshot.v2'),
    route_variant_id: uuidSchema,
    origin: routeDraftEndpointSchema,
    via: z.array(routeDraftEndpointSchema).max(MAX_ROUTE_VIA_POINTS),
    destination: routeDraftEndpointSchema.nullable(),
    route_selection: routeDraftSelectionSchema.nullable(),
    route_seed: routeSeedSchema.nullable(),
    route_copy_source: routeCopySourceProofSchema.nullable(),
    distance_input_mode: routeDraftDistanceInputModeSchema,
    distance_metres: recordedMetresSchema.nullable(),
})
    .superRefine((value, context) => {
    validateRouteSeedBinding(value, context);
    validateDistanceBinding(value, context);
    validateRouteCopySourceDraftBinding(value, context);
});
export const routeDraftSnapshotSchema = z.discriminatedUnion('snapshot_version', [
    routeDraftSnapshotV2Schema,
    routeDraftSnapshotV1Schema,
]);
function validateRouteCopySourceDraftBinding(value, context) {
    if (value.route_copy_source === null)
        return;
    if (value.route_selection?.kind !== 'offer' ||
        value.route_selection.offer_source !== 'history' ||
        value.route_selection.selection_method !== 'explicit' ||
        value.route_seed === null) {
        context.addIssue({
            code: 'custom',
            path: ['route_copy_source'],
            message: 'a durable route-copy proof requires an explicit seeded history selection',
        });
    }
}
export function validateRouteDraftCatalogProvenancePair(value, context) {
    if ((value.catalog_document_id === null) !== (value.catalog_version === null)) {
        context.addIssue({
            code: 'custom',
            path: [value.catalog_document_id === null ? 'catalog_document_id' : 'catalog_version'],
            message: 'catalog_document_id and catalog_version must be both present or both null',
        });
    }
}
export function routeDraftEndpointLocationSnapshot(endpointInput) {
    const endpoint = routeDraftEndpointSchema.parse(endpointInput);
    if (endpoint.carried_location_snapshot !== null)
        return endpoint.carried_location_snapshot;
    if (endpoint.name.trim() === '' || endpoint.name.trim() !== endpoint.name)
        return null;
    return locationSnapshotSchema.parse({
        location_id: endpoint.location_id,
        display_name: endpoint.name,
        location_type: 'operational_point',
        country_code: endpoint.country_code,
        key_identifiers: endpoint.key_identifier === null ? [] : [endpoint.key_identifier],
    });
}
function validateCarriedLocationSnapshot(value, context) {
    const snapshot = value.carried_location_snapshot;
    if (snapshot === null)
        return;
    if (snapshot.location_id !== value.location_id) {
        context.addIssue({
            code: 'custom',
            path: ['carried_location_snapshot', 'location_id'],
            message: 'a carried location snapshot must retain its historical location identity',
        });
    }
    if (snapshot.display_name !== value.name) {
        context.addIssue({
            code: 'custom',
            path: ['carried_location_snapshot', 'display_name'],
            message: 'a carried location snapshot name must not be edited in place',
        });
    }
    if (snapshot.country_code !== value.country_code) {
        context.addIssue({
            code: 'custom',
            path: ['carried_location_snapshot', 'country_code'],
            message: 'a carried location snapshot country must not be edited in place',
        });
    }
    if (!sameNullableIdentifier(snapshot.key_identifiers[0] ?? null, value.key_identifier)) {
        context.addIssue({
            code: 'custom',
            path: ['key_identifier'],
            message: 'a carried location snapshot identifier must not be edited in place',
        });
    }
}
function validateRouteSeedBinding(value, context) {
    if (value.route_selection?.kind === 'manual' && value.route_seed !== null) {
        context.addIssue({
            code: 'custom',
            path: ['route_seed'],
            message: 'a manually selected route cannot retain inherited route seed provenance',
        });
    }
    if (value.route_selection?.kind === 'offer' && value.route_seed === null) {
        context.addIssue({
            code: 'custom',
            path: ['route_seed'],
            message: 'an offered route selection requires its immutable route seed',
        });
    }
    if (value.route_selection?.kind === 'offer' &&
        value.route_selection.offer_source === 'network' &&
        value.route_seed?.candidate_source !== 'network') {
        context.addIssue({
            code: 'custom',
            path: ['route_seed', 'candidate_source'],
            message: 'a network offer must retain network candidate provenance',
        });
    }
    if (value.route_seed === null)
        return;
    if (value.via.length !== value.route_seed.via.length ||
        value.via.some((endpoint, index) => {
            const snapshot = value.route_seed?.via[index];
            return (snapshot === undefined ||
                endpoint.carried_location_snapshot === null ||
                canonicalJsonStringify(endpoint.carried_location_snapshot) !==
                    canonicalJsonStringify(snapshot));
        })) {
        context.addIssue({
            code: 'custom',
            path: ['via'],
            message: 'draft via inputs must exactly retain immutable ordered route-seed snapshots',
        });
    }
}
function validateDistanceBinding(value, context) {
    if (value.distance_input_mode === 'none' && value.distance_metres !== null) {
        context.addIssue({
            code: 'custom',
            path: ['distance_metres'],
            message: 'none distance input mode requires null distance_metres',
        });
    }
    if (value.distance_input_mode === 'inherited') {
        if (value.route_seed === null) {
            context.addIssue({
                code: 'custom',
                path: ['route_seed'],
                message: 'inherited distance input mode requires route_seed',
            });
        }
        else if (value.distance_metres !== value.route_seed.distance.metres) {
            context.addIssue({
                code: 'custom',
                path: ['distance_metres'],
                message: 'inherited distance must exactly match route_seed.distance.metres',
            });
        }
    }
    if (value.distance_input_mode === 'manual_override' && value.distance_metres === null) {
        context.addIssue({
            code: 'custom',
            path: ['distance_metres'],
            message: 'manual_override distance input mode requires distance_metres',
        });
    }
}
function sameNullableIdentifier(left, right) {
    return (left === right ||
        (left !== null &&
            right !== null &&
            left.namespace === right.namespace &&
            left.namespace_other === right.namespace_other &&
            left.value === right.value));
}
//# sourceMappingURL=route-draft.js.map